import { API_URL } from '../config';
import { Budget, BudgetLine, BudgetType, BudgetStatus } from '@akfdev-com/shared/src/models';
import { getApiOwnerId } from '../services/api';
import { displayApiErrorFromResponse } from '../utils/apiErrorHandler';

/**
 * Request types for Budget API operations
 */

export interface CreateBudgetRequest {
  name: string;
  startDate: string; // ISO 8601: YYYY-MM-DD
  endDate: string; // ISO 8601: YYYY-MM-DD
  budgetType: BudgetType;
  notes?: string;
  lines: CreateBudgetLineRequest[];
}

export interface CreateBudgetLineRequest {
  categoryId: string;
  budgetAmount: number; // in pence
  notes?: string;
}

export interface UpdateBudgetRequest {
  name?: string;
  startDate?: string;
  endDate?: string;
  budgetType?: BudgetType;
  status?: BudgetStatus;
  notes?: string;
}

export interface UpdateBudgetLineRequest {
  categoryId?: string;
  budgetAmount?: number; // in pence
  notes?: string;
}

export interface CopyBudgetRequest {
  startDate: string; // ISO 8601: YYYY-MM-DD
  endDate: string; // ISO 8601: YYYY-MM-DD
  name?: string; // optional new name, defaults to "Copy of {original name}"
}

export interface VarianceLine {
  categoryId: string;
  categoryName: string;
  budgetAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number;
}

export interface VarianceResponse {
  budgetId: string;
  period: {
    startDate: string;
    endDate: string;
  };
  lines: VarianceLine[];
  totalBudgeted: number;
  totalActual: number;
  totalVariance: number;
}

function authHeaders(token: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const ownerId = getApiOwnerId();
  if (ownerId) {
    headers['X-Clerk-Subdomain'] = ownerId;
  }
  return headers;
}

// Parses JSON but throws a clear error if the response is HTML (e.g. CloudFront
// error pages that masquerade as 200 when a route doesn't exist in API Gateway).
async function parseJsonResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(`API returned non-JSON response (HTTP ${response.status}) — the endpoint may not be deployed yet`);
  }
  return response.json() as Promise<T>;
}

// The budgets Lambda wraps write responses (POST/PUT/copy) as
// `{ message, budget }` while read responses (GET) return the budget object
// directly. These helpers normalise both shapes so callers always receive the
// underlying entity. Without this, `newBudget.id` is undefined after create,
// causing navigation to `/budgets/undefined`.
function unwrapBudget(body: unknown): Budget {
  if (body && typeof body === 'object' && 'budget' in body) {
    return (body as { budget: Budget }).budget;
  }
  return body as Budget;
}

function unwrapBudgetLine(body: unknown): BudgetLine {
  if (body && typeof body === 'object' && 'line' in body) {
    return (body as { line: BudgetLine }).line;
  }
  return body as BudgetLine;
}

/**
 * Budgets API Service
 * 
 * Provides methods for all 12 budget-related API endpoints:
 * - CRUD operations for budgets
 * - Budget line management
 * - Budget actions (copy, approve)
 * - Variance reporting
 *
 * On a non-ok response every method routes the failure through
 * `displayApiErrorFromResponse`, which surfaces the shared ErrorModal/ErrorToast
 * with a visible, copyable Error ID before throwing `ErrorDisplayed`.
 */

/**
 * List all budgets for a building
 * GET /buildings/{buildingId}/budgets
 */
export const listBudgets = async (
  buildingId: string,
  token: string
): Promise<Budget[]> => {
  try {
    const response = await fetch(`${API_URL}/buildings/${buildingId}/budgets`, {
      headers: authHeaders(token),
    });

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return parseJsonResponse<Budget[]>(response);
  } catch (error) {
    console.error(`Error listing budgets for building ${buildingId}:`, error);
    throw error;
  }
};

/**
 * Get a single budget by ID
 * GET /buildings/{buildingId}/budgets/{id}
 */
export const getBudget = async (
  buildingId: string,
  budgetId: string,
  token: string
): Promise<Budget> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}`,
      { headers: authHeaders(token) }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return parseJsonResponse<Budget>(response);
  } catch (error) {
    console.error(`Error fetching budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Create a new budget
 * POST /buildings/{buildingId}/budgets
 */
export const createBudget = async (
  buildingId: string,
  data: CreateBudgetRequest,
  token: string
): Promise<Budget> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets`,
      {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return unwrapBudget(await parseJsonResponse<unknown>(response));
  } catch (error) {
    console.error('Error creating budget:', error);
    throw error;
  }
};

/**
 * Update an existing budget
 * PUT /buildings/{buildingId}/budgets/{id}
 */
export const updateBudget = async (
  buildingId: string,
  budgetId: string,
  data: UpdateBudgetRequest,
  token: string
): Promise<Budget> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}`,
      {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return unwrapBudget(await parseJsonResponse<unknown>(response));
  } catch (error) {
    console.error(`Error updating budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Delete a budget
 * DELETE /buildings/{buildingId}/budgets/{id}
 */
export const deleteBudget = async (
  buildingId: string,
  budgetId: string,
  token: string
): Promise<void> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}`,
      {
        method: 'DELETE',
        headers: authHeaders(token),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }
  } catch (error) {
    console.error(`Error deleting budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Copy a budget as a template for a new period
 * POST /buildings/{buildingId}/budgets/{id}/copy
 */
export const copyBudget = async (
  buildingId: string,
  budgetId: string,
  data: CopyBudgetRequest,
  token: string
): Promise<Budget> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}/copy`,
      {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return unwrapBudget(await parseJsonResponse<unknown>(response));
  } catch (error) {
    console.error(`Error copying budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Approve a budget
 * POST /buildings/{buildingId}/budgets/{id}/approve
 */
export const approveBudget = async (
  buildingId: string,
  budgetId: string,
  token: string
): Promise<Budget> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}/approve`,
      {
        method: 'POST',
        headers: authHeaders(token),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return unwrapBudget(await parseJsonResponse<unknown>(response));
  } catch (error) {
    console.error(`Error approving budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Get all budget lines for a budget
 * GET /buildings/{buildingId}/budgets/{id}/lines
 */
export const getBudgetLines = async (
  buildingId: string,
  budgetId: string,
  token: string
): Promise<BudgetLine[]> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}/lines`,
      { headers: authHeaders(token) }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return parseJsonResponse<BudgetLine[]>(response);
  } catch (error) {
    console.error(`Error fetching lines for budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Create a new budget line
 * POST /buildings/{buildingId}/budgets/{id}/lines
 */
export const createBudgetLine = async (
  buildingId: string,
  budgetId: string,
  data: CreateBudgetLineRequest,
  token: string
): Promise<BudgetLine> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}/lines`,
      {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return unwrapBudgetLine(await parseJsonResponse<unknown>(response));
  } catch (error) {
    console.error(`Error creating budget line for budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Update an existing budget line
 * PUT /buildings/{buildingId}/budgets/{id}/lines/{lineId}
 */
export const updateBudgetLine = async (
  buildingId: string,
  budgetId: string,
  lineId: string,
  data: UpdateBudgetLineRequest,
  token: string
): Promise<BudgetLine> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}/lines/${lineId}`,
      {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return unwrapBudgetLine(await parseJsonResponse<unknown>(response));
  } catch (error) {
    console.error(`Error updating budget line ${lineId}:`, error);
    throw error;
  }
};

/**
 * Delete a budget line
 * DELETE /buildings/{buildingId}/budgets/{id}/lines/{lineId}
 */
export const deleteBudgetLine = async (
  buildingId: string,
  budgetId: string,
  lineId: string,
  token: string
): Promise<void> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}/lines/${lineId}`,
      {
        method: 'DELETE',
        headers: authHeaders(token),
      }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }
  } catch (error) {
    console.error(`Error deleting budget line ${lineId}:`, error);
    throw error;
  }
};

/**
 * Get budget variance report (budget vs actual)
 * GET /buildings/{buildingId}/budgets/{id}/variance
 */
export const getBudgetVariance = async (
  buildingId: string,
  budgetId: string,
  token: string
): Promise<VarianceResponse> => {
  try {
    const response = await fetch(
      `${API_URL}/buildings/${buildingId}/budgets/${budgetId}/variance`,
      { headers: authHeaders(token) }
    );

    if (!response.ok) {
      await displayApiErrorFromResponse(response);
    }

    return parseJsonResponse<VarianceResponse>(response);
  } catch (error) {
    console.error(`Error fetching variance for budget ${budgetId}:`, error);
    throw error;
  }
};

/**
 * Budgets API - consolidated export
 */
export const budgetsApi = {
  list: listBudgets,
  get: getBudget,
  create: createBudget,
  update: updateBudget,
  delete: deleteBudget,
  copy: copyBudget,
  approve: approveBudget,
  getLines: getBudgetLines,
  createLine: createBudgetLine,
  updateLine: updateBudgetLine,
  deleteLine: deleteBudgetLine,
  getVariance: getBudgetVariance,
};

export default budgetsApi;
